import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Stack,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon,
  Audiotrack as SpotifyIcon,
  YouTube as YouTubeIcon,
  LiveTv as LiveTvIcon,
  Business as BusinessIcon,
  Explore as ExploreIcon,
  Podcasts as PodcastsIcon,
  Collections as CollectionsIcon,
  CompareArrows as CompareArrowsIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  ContentCopy as ContentCopyIcon,
  Send as SendIcon,
  LinkOff as LinkOffIcon,
  Tune as TuneIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import CompanyPermissionsPanel, { ChatProductToggle } from "../components/CompanyPermissionsPanel";
import CompanyChatProfilePanel from "../components/CompanyChatProfilePanel";
import CompanyImageEditor from "../components/CompanyImageEditor";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import apiService, {
  User,
  Podcast,
  CompanyPodcastProgramRow,
  SocialAccount,
  SocialPlatform,
  ExplorerBrandInvestment,
  ExplorerBrandInvestmentPoints,
} from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { productBrand } from "../productBrand";
import { BRAND_CATEGORY_LABELS } from "../constants/brandCategories";
import {
  ContentPlanScopesEditor,
  blankContentPlanScope,
  type ContentPlanScopeRow,
} from "../components/ContentPlanScopesEditor";

const BRAND_INVESTMENT_POINT_FIELDS: Array<{
  key: keyof ExplorerBrandInvestmentPoints;
  label: string;
  helperText: string;
}> = [
  {
    key: "credits",
    label: "Créditos",
    helperText: "Apertura y/o cierre",
  },
  {
    key: "content",
    label: "Mención en contenido",
    helperText: "Mención dentro del episodio",
  },
  { key: "banner", label: "Banner", helperText: "Solo banner / overlay" },
  { key: "placement", label: "Placement", helperText: "Placement físico" },
];

const DEFAULT_BRAND_INVESTMENT_POINTS: Record<
  keyof ExplorerBrandInvestmentPoints,
  string
> = {
  credits: "1",
  content: "3",
  banner: "1",
  placement: "2",
};

function brandInvestmentPointsToForm(
  points: ExplorerBrandInvestmentPoints | undefined,
): Record<keyof ExplorerBrandInvestmentPoints, string> {
  if (!points) return DEFAULT_BRAND_INVESTMENT_POINTS;
  return {
    credits: String(points.credits ?? DEFAULT_BRAND_INVESTMENT_POINTS.credits),
    content: String(points.content ?? DEFAULT_BRAND_INVESTMENT_POINTS.content),
    banner: String(points.banner ?? DEFAULT_BRAND_INVESTMENT_POINTS.banner),
    placement: String(
      points.placement ?? DEFAULT_BRAND_INVESTMENT_POINTS.placement,
    ),
  };
}

// Explorer tab products (for per-tab permissions) - IDs stored in English
const EXPLORER_TAB_PRODUCTS = [
  {
    id: "explorer-programs",
    name: "Programas",
    description: "Acceso al tab Programas del Explorador",
    icon: PodcastsIcon,
  },
  {
    id: "explorer-sponsors",
    name: "Auspiciadores",
    description: "Acceso al tab Auspiciadores del Explorador",
    icon: BusinessIcon,
  },
  {
    id: "explorer-livestreams",
    name: "Livestreams",
    description: "Acceso al tab Livestreams del Explorador",
    icon: LiveTvIcon,
  },
  {
    id: "explorer-compare",
    name: "Comparar",
    description: "Acceso al tab Comparar del Explorador",
    icon: CompareArrowsIcon,
  },
] as const;

const ClearableDateField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  helperText?: React.ReactNode;
  clearTitle?: string;
}> = ({
  label,
  value,
  onChange,
  helperText,
  clearTitle = "Quitar fecha",
}) => (
  <Stack
    direction="row"
    alignItems={helperText ? "flex-start" : "center"}
    spacing={0.25}
  >
    <TextField
      size="small"
      type="date"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      sx={{ width: 170 }}
    />
    <Tooltip title={clearTitle}>
      <span>
        <IconButton
          size="small"
          aria-label={clearTitle}
          disabled={!value}
          onClick={() => onChange("")}
          sx={{ mt: helperText ? 0.5 : 0 }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </Stack>
);

// All available products (explorer full + explorer tabs + mispodcasts + micontenido + tracker)
const AVAILABLE_PRODUCTS = [
  {
    id: "explorer",
    name: "Explorador (todos los tabs)",
    description: "Acceso completo al Explorador (todos los tabs)",
    icon: ExploreIcon,
  },
  ...EXPLORER_TAB_PRODUCTS,
  {
    id: "mispodcasts",
    name: "Mis Podcasts",
    description: "Acceso al producto Mis Podcasts para análisis privado",
    icon: PodcastsIcon,
  },
  {
    id: "micontenido",
    name: "Mi contenido",
    description: "Acceso al panel Mi contenido (independiente de Mis Podcasts)",
    icon: CollectionsIcon,
  },
  {
    id: "tracker",
    name: "Tracker",
    description: "Acceso al Tracker de marcas para seguimiento de menciones",
    icon: ExploreIcon,
  },
  {
    id: "comparador",
    name: "Comparador",
    description: "Acceso al Comparador de podcasts, programas y marcas",
    icon: CompareArrowsIcon,
  },
  {
    id: "clips",
    name: "Clips",
    description:
      "Colecciones de Shorts, Reels y TikToks por URL (compartibles)",
    icon: CollectionsIcon,
  },
] as const;

function adminPlatformLabel(platform: SocialPlatform): string {
  switch (platform) {
    case "meta":
      return "Instagram / Meta";
    case "tiktok":
      return "TikTok";
    case "youtube":
      return "YouTube";
    case "spotify":
      return "Spotify";
    default:
      return platform;
  }
}

/** OAuth-linked rows from `social_accounts` (same source as Ajustes → Mis canales). */
const PodcastOAuthConnectionsAdmin: React.FC<{
  podcast: Podcast;
  companyId: string;
}> = ({ podcast, companyId }) => {
  const queryClient = useQueryClient();
  const [pendingUnlink, setPendingUnlink] = useState<SocialAccount | null>(
    null,
  );
  const [unlinkErr, setUnlinkErr] = useState<string | null>(null);

  const {
    data: accounts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-podcast-social-accounts", companyId, podcast._id],
    queryFn: () =>
      apiService.getSocialAccountsByPodcast(podcast._id, { companyId }),
    enabled: !!podcast._id && !!companyId,
    staleTime: 60_000,
  });

  const unlinkMutation = useMutation({
    mutationFn: (id: string) => apiService.unlinkSocialAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-podcast-social-accounts", companyId, podcast._id],
      });
      setPendingUnlink(null);
      setUnlinkErr(null);
    },
    onError: (e: any) => {
      setUnlinkErr(
        e?.response?.data?.message ||
          e?.message ||
          "No se pudo quitar la conexión",
      );
    },
  });

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        bgcolor: "action.hover",
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        Cuentas conectadas (OAuth)
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1.5 }}
      >
        Mismas conexiones que en Ajustes del cliente (Mis canales). Quitar aquí
        elimina el vínculo y los tokens en AWS para ese canal.
      </Typography>
      {unlinkErr && (
        <Alert
          severity="error"
          sx={{ mb: 1, py: 0 }}
          onClose={() => setUnlinkErr(null)}
        >
          {unlinkErr}
        </Alert>
      )}
      {isLoading ? (
        <CircularProgress size={22} />
      ) : error ? (
        <Alert severity="error" sx={{ py: 0 }}>
          No se pudieron cargar las cuentas OAuth
        </Alert>
      ) : !accounts?.length ? (
        <Typography variant="body2" color="text.secondary">
          Ninguna cuenta OAuth enlazada.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Plataforma</TableCell>
              <TableCell>Cuenta</TableCell>
              <TableCell>Expira token</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((a) => (
              <TableRow key={a._id}>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {a.platform === "meta" ? (
                      <InstagramIcon
                        fontSize="small"
                        sx={{ color: "#E1306C" }}
                      />
                    ) : a.platform === "tiktok" ? (
                      <TikTokIcon fontSize="small" />
                    ) : a.platform === "youtube" ? (
                      <YouTubeIcon fontSize="small" sx={{ color: "#FF0000" }} />
                    ) : (
                      <SpotifyIcon fontSize="small" sx={{ color: "#1DB954" }} />
                    )}
                    <span>{adminPlatformLabel(a.platform)}</span>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                  >
                    {a.username ? `@${a.username.replace(/^@/, "")}` : "—"} ·{" "}
                    {a.accountId}
                  </Typography>
                </TableCell>
                <TableCell>
                  {a.tokenExpiresAt
                    ? new Date(a.tokenExpiresAt).toLocaleString("es-CL", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Quitar conexión OAuth">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setPendingUnlink(a)}
                      disabled={unlinkMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={!!pendingUnlink}
        onClose={() => !unlinkMutation.isPending && setPendingUnlink(null)}
      >
        <DialogTitle>Quitar conexión OAuth</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se eliminará el vínculo para{" "}
            <strong>
              {pendingUnlink ? adminPlatformLabel(pendingUnlink.platform) : ""}
            </strong>
            {pendingUnlink?.username
              ? ` (@${pendingUnlink.username.replace(/^@/, "")})`
              : ""}
            . Los tokens asociados se borrarán del almacén seguro.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingUnlink(null)}
            disabled={unlinkMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={unlinkMutation.isPending || !pendingUnlink}
            onClick={() =>
              pendingUnlink && unlinkMutation.mutate(pendingUnlink._id)
            }
          >
            Quitar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PodcastLivestreamToggle: React.FC<{
  podcast: Podcast;
  companyId: string;
  onError?: (message: string) => void;
}> = ({ podcast, companyId, onError }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["podcast-settings", companyId, podcast._id],
    queryFn: () =>
      apiService.getCompanyPodcastSettings(companyId, podcast._id),
    enabled: !!companyId && !!podcast._id,
    staleTime: 60_000,
  });
  const mutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiService.updateCompanyPodcastLivestreamSettings(
        companyId,
        podcast._id,
        enabled,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["podcast-settings", companyId, podcast._id],
      });
      queryClient.invalidateQueries({ queryKey: ["collection-detail"] });
    },
    onError: (e: any) =>
      onError?.(
        e?.response?.data?.message || "Error actualizando livestream del canal",
      ),
  });
  return (
    <Tooltip
      title="Habilita métricas de livestream para colecciones que cubren todo el canal (sin filtro de programa)."
      arrow
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={data?.livestreamDataEnabled ?? false}
            onChange={(e) => mutation.mutate(e.target.checked)}
            disabled={isLoading || mutation.isPending}
            size="small"
          />
        }
        label="Datos livestream (canal)"
        sx={{ m: 0 }}
      />
    </Tooltip>
  );
};

const CompanyConfig: React.FC = () => {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const queryClient = useQueryClient();
  const { refreshCompanyProducts, user: authUser } = useAuth();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Product access state
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [enablePerUserPermissions, setEnablePerUserPermissions] =
    useState<boolean>(false);
  const [companyDateFilter, setCompanyDateFilter] = useState<string>("");
  const [companyDateMaxFilter, setCompanyDateMaxFilter] = useState<string>("");
  const dateFilterDraftRef = React.useRef({ min: "", max: "" });
  const dateFilterDebounceTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [miContenidoDateFilterMin, setMiContenidoDateFilterMin] =
    useState<string>("");
  const [miContenidoDateFilterMax, setMiContenidoDateFilterMax] =
    useState<string>("");
  const miContenidoDateFilterDraftRef = React.useRef({ min: "", max: "" });
  const miContenidoDateFilterDebounceTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  // User management state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [roleInput, setRoleInput] = useState<"admin" | "member">("member");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [linkInviteRole, setLinkInviteRole] = useState<"admin" | "member">(
    "member",
  );
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState<string | null>(
    null,
  );
  const [inviteUrlCopied, setInviteUrlCopied] = useState(false);
  const [generatedUrlKind, setGeneratedUrlKind] = useState<"invite" | "demo">(
    "invite",
  );
  const [demoName, setDemoName] = useState("");
  const [demoEmail, setDemoEmail] = useState("");
  const [demoRole, setDemoRole] = useState<"admin" | "member">("member");
  const [pendingDeleteDemoUser, setPendingDeleteDemoUser] = useState<{
    _id: string;
    email: string;
  } | null>(null);
  const [pendingRevokeDemoInvite, setPendingRevokeDemoInvite] = useState<{
    _id: string;
    email: string;
  } | null>(null);
  const [localhostInviteSnackbarOpen, setLocalhostInviteSnackbarOpen] =
    useState(false);

  const isLocalhost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  // Podcast management state
  const [selectedPodcast, setSelectedPodcast] = useState<Podcast | null>(null);
  const [podcastSearch, setPodcastSearch] = useState("");

  // Social accounts dialog state
  const [socialAccountsDialog, setSocialAccountsDialog] = useState<{
    open: boolean;
    podcast: Podcast | null;
    instagram: string;
    tiktok: string;
    facebook: string;
  }>({
    open: false,
    podcast: null,
    instagram: "",
    tiktok: "",
    facebook: "",
  });

  // Tracker brand (company-level default; set when tracker product is enabled)
  const [companyTrackerBrandId, setCompanyTrackerBrandId] = useState<
    string | null
  >(null);
  const [trackerBrandDialogOpen, setTrackerBrandDialogOpen] = useState(false);
  /** Mi Contenido brand allowlist (empty = all brands) */
  const [miContenidoBrandIds, setMiContenidoBrandIds] = useState<string[]>([]);
  /** Locked Explorer Auspiciadores brand category (empty = all) */
  const [explorerSponsorCategoryFilter, setExplorerSponsorCategoryFilter] =
    useState("");
  /** Explorer brand investment estimates */
  const [brandInvestmentEnabled, setBrandInvestmentEnabled] = useState(false);
  const [brandInvestmentPoints, setBrandInvestmentPoints] = useState(
    DEFAULT_BRAND_INVESTMENT_POINTS,
  );
  const [brandInvestmentClpPerPoint, setBrandInvestmentClpPerPoint] =
    useState("");

  // Comparador podcast IDs
  const [comparadorPodcastIds, setComparadorPodcastIds] = useState<string[]>(
    [],
  );

  // Sidebar help/guides link
  const [sidebarGuidesUrl, setSidebarGuidesUrl] = useState("");
  const [sidebarGuidesLabel, setSidebarGuidesLabel] = useState("");

  // Pipeline content plan (programs + brands per podcast)
  const [pipelinePlanEnabled, setPipelinePlanEnabled] = useState(true);
  const [pipelineScopes, setPipelineScopes] = useState<ContentPlanScopeRow[]>([
    blankContentPlanScope(),
  ]);

  const [deleteCompanyDialogOpen, setDeleteCompanyDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<{
    _id: string;
    name: string;
    email: string;
    isDemo?: boolean;
  } | null>(null);
  const [pendingDeleteInvite, setPendingDeleteInvite] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [userFiltersDialogOpen, setUserFiltersDialogOpen] = useState(false);
  const [userFiltersTarget, setUserFiltersTarget] = useState<User | null>(null);
  const [userFilterDateMin, setUserFilterDateMin] = useState("");
  const [userFilterDateMax, setUserFilterDateMax] = useState("");
  const [userFilterMiMin, setUserFilterMiMin] = useState("");
  const [userFilterMiMax, setUserFilterMiMax] = useState("");
  /** `__inherit__` = use company; `` = force Todas; otherwise category slug */
  const [userFilterSponsorCategory, setUserFilterSponsorCategory] =
    useState("__inherit__");
  const [trackerBrandPick, setTrackerBrandPick] = useState<{
    _id: string;
    name: string;
  } | null>(null);
  const [trackerBrandSearch, setTrackerBrandSearch] = useState("");

  // Fetch company details
  const {
    data: company,
    isLoading: companyLoading,
    error: companyError,
  } = useQuery({
    queryKey: ["company-detail", companyId],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.getCompanyById(companyId);
    },
    enabled: !!companyId,
  });

  const { data: companyContentPlan, isLoading: contentPlanLoading } =
    useQuery({
      queryKey: ["company-content-plan", companyId],
      queryFn: () => apiService.getCompanyContentPlan(companyId!),
      enabled: !!companyId,
    });

  React.useEffect(() => {
    if (contentPlanLoading) return;
    if (companyContentPlan) {
      setPipelinePlanEnabled(companyContentPlan.enabled);
      setPipelineScopes(
        companyContentPlan.scopes.length > 0
          ? companyContentPlan.scopes.map((s) => ({
              podcastId: s.podcastId,
              programIds: s.programIds ?? [],
              brandIds: s.brandIds ?? [],
            }))
          : [blankContentPlanScope()],
      );
    } else {
      setPipelinePlanEnabled(true);
      setPipelineScopes([blankContentPlanScope()]);
    }
  }, [companyContentPlan, contentPlanLoading]);

  // Initialize form values when company data loads
  React.useEffect(() => {
    if (company) {
      setSelectedProducts((company as any).products || []);
      setEnablePerUserPermissions(
        (company as any).enablePerUserProductPermissions || false,
      );
      const min = (company as any).dateFilterMinStartDate || "";
      const max = (company as any).dateFilterMaxEndDate || "";
      setCompanyDateFilter(min);
      setCompanyDateMaxFilter(max);
      dateFilterDraftRef.current = { min, max };
      const miMin = (company as any).miContenidoDateFilterMinStartDate || "";
      const miMax = (company as any).miContenidoDateFilterMaxEndDate || "";
      setMiContenidoDateFilterMin(miMin);
      setMiContenidoDateFilterMax(miMax);
      miContenidoDateFilterDraftRef.current = { min: miMin, max: miMax };
      setCompanyTrackerBrandId((company as any).trackerBrandId ?? null);
      setMiContenidoBrandIds((company as any).miContenidoBrandIds || []);
      setExplorerSponsorCategoryFilter(
        (company as any).explorerSponsorCategoryFilter || "",
      );
      const inv = (company as any).explorerBrandInvestment;
      setBrandInvestmentEnabled(Boolean(inv?.enabled));
      setBrandInvestmentPoints(brandInvestmentPointsToForm(inv?.points));
      setBrandInvestmentClpPerPoint(
        inv?.clpPerPoint ? String(inv.clpPerPoint) : "",
      );
      setComparadorPodcastIds((company as any).comparadorPodcastIds || []);
      setSidebarGuidesUrl((company as any).sidebarGuidesUrl || "");
      setSidebarGuidesLabel((company as any).sidebarGuidesLabel || "");
    }
  }, [company]);

  // Update products mutation
  const updateProductsMutation = useMutation({
    mutationFn: async (products: string[]) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyProducts(companyId, products);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      await refreshCompanyProducts();
      setSuccessMessage("Acceso a productos actualizado");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || "Error actualizando productos"),
  });

  // Update per-user permissions mutation
  const updatePerUserPermissionsMutation = useMutation({
    mutationFn: async (enablePerUserProductPermissions: boolean) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyPerUserPermissions(
        companyId,
        enablePerUserProductPermissions,
      );
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      await refreshCompanyProducts();
      setSuccessMessage("Permisos por usuario actualizados");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || "Error actualizando permisos por usuario",
      ),
  });

  // Update user products mutation
  const updateUserProductsMutation = useMutation({
    mutationFn: async ({
      userId,
      products,
    }: {
      userId: string;
      products: string[];
    }) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateUserProducts(companyId, userId, products);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["company-users", companyId] });
      await refreshCompanyProducts();
      setSuccessMessage("Productos del usuario actualizados");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          "Error actualizando productos del usuario",
      ),
  });

  const openUserFiltersDialog = (u: User) => {
    setUserFiltersTarget(u);
    setUserFilterDateMin(u.dateFilterMinStartDate || "");
    setUserFilterDateMax(u.dateFilterMaxEndDate || "");
    setUserFilterMiMin(u.miContenidoDateFilterMinStartDate || "");
    setUserFilterMiMax(u.miContenidoDateFilterMaxEndDate || "");
    if (
      u.explorerSponsorCategoryFilter === undefined ||
      u.explorerSponsorCategoryFilter === null
    ) {
      setUserFilterSponsorCategory("__inherit__");
    } else {
      setUserFilterSponsorCategory(u.explorerSponsorCategoryFilter);
    }
    setUserFiltersDialogOpen(true);
  };

  const updateUserFiltersMutation = useMutation({
    mutationFn: async () => {
      if (!companyId || !userFiltersTarget) {
        throw new Error("Company ID and user required");
      }
      return await apiService.updateUserFilterOverrides(
        companyId,
        userFiltersTarget._id,
        {
          dateFilterMinStartDate: userFilterDateMin || null,
          dateFilterMaxEndDate: userFilterDateMax || null,
          miContenidoDateFilterMinStartDate: userFilterMiMin || null,
          miContenidoDateFilterMaxEndDate: userFilterMiMax || null,
          explorerSponsorCategoryFilter:
            userFilterSponsorCategory === "__inherit__"
              ? null
              : userFilterSponsorCategory,
        },
      );
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["company-users", companyId] });
      setUserFiltersDialogOpen(false);
      setUserFiltersTarget(null);
      setSuccessMessage("Filtros del usuario actualizados");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || "Error actualizando filtros del usuario",
      ),
  });

  // Update company date filter mutation (sends both min and max)
  const updateCompanyDateFilterMutation = useMutation({
    mutationFn: async ({
      dateFilterMinStartDate,
      dateFilterMaxEndDate,
    }: {
      dateFilterMinStartDate: string | null;
      dateFilterMaxEndDate: string | null;
    }) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyDateFilter(
        companyId,
        dateFilterMinStartDate,
        dateFilterMaxEndDate,
      );
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      await refreshCompanyProducts();
      setSuccessMessage("Filtro de fechas actualizado");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || "Error actualizando filtro de fechas",
      ),
  });

  const updateCompanyMiContenidoDateFilterMutation = useMutation({
    mutationFn: async ({
      miContenidoDateFilterMinStartDate,
      miContenidoDateFilterMaxEndDate,
    }: {
      miContenidoDateFilterMinStartDate: string | null;
      miContenidoDateFilterMaxEndDate: string | null;
    }) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyMiContenidoDateFilter(
        companyId,
        miContenidoDateFilterMinStartDate,
        miContenidoDateFilterMaxEndDate,
      );
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      await refreshCompanyProducts();
      setSuccessMessage("Filtro Mi contenido actualizado");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || "Error actualizando filtro Mi contenido",
      ),
  });

  const scheduleDateFilterPersist = React.useCallback(() => {
    if (dateFilterDebounceTimerRef.current)
      clearTimeout(dateFilterDebounceTimerRef.current);
    dateFilterDebounceTimerRef.current = setTimeout(() => {
      dateFilterDebounceTimerRef.current = null;
      const { min, max } = dateFilterDraftRef.current;
      updateCompanyDateFilterMutation.mutate({
        dateFilterMinStartDate: min || null,
        dateFilterMaxEndDate: max || null,
      });
    }, 450);
  }, [updateCompanyDateFilterMutation]);

  const scheduleMiContenidoDateFilterPersist = React.useCallback(() => {
    if (miContenidoDateFilterDebounceTimerRef.current)
      clearTimeout(miContenidoDateFilterDebounceTimerRef.current);
    miContenidoDateFilterDebounceTimerRef.current = setTimeout(() => {
      miContenidoDateFilterDebounceTimerRef.current = null;
      const { min, max } = miContenidoDateFilterDraftRef.current;
      updateCompanyMiContenidoDateFilterMutation.mutate({
        miContenidoDateFilterMinStartDate: min || null,
        miContenidoDateFilterMaxEndDate: max || null,
      });
    }, 450);
  }, [updateCompanyMiContenidoDateFilterMutation]);

  React.useEffect(() => {
    return () => {
      if (dateFilterDebounceTimerRef.current)
        clearTimeout(dateFilterDebounceTimerRef.current);
      if (miContenidoDateFilterDebounceTimerRef.current)
        clearTimeout(miContenidoDateFilterDebounceTimerRef.current);
    };
  }, []);

  // Update company tracker brand mutation
  const updateTrackerBrandMutation = useMutation({
    mutationFn: async (trackerBrandId: string | null) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyTrackerBrand(
        companyId,
        trackerBrandId,
      );
    },
    onSuccess: async (_, newId) => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      setCompanyTrackerBrandId(newId);
      setTrackerBrandDialogOpen(false);
      setTrackerBrandPick(null);
      await refreshCompanyProducts();
      setSuccessMessage(
        newId == null
          ? "Marca del Tracker eliminada"
          : "Marca del Tracker actualizada",
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || "Error actualizando marca del Tracker",
      ),
  });

  // Update Mi Contenido brand allowlist
  const updateMiContenidoBrandsMutation = useMutation({
    mutationFn: async (brandIds: string[]) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyMiContenidoBrands(
        companyId,
        brandIds,
      );
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      setMiContenidoBrandIds(data.miContenidoBrandIds || []);
      setSuccessMessage(
        data.miContenidoBrandIds?.length
          ? "Marcas de Mi Contenido actualizadas"
          : "Restricción de marcas de Mi Contenido eliminada",
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          "Error actualizando marcas de Mi Contenido",
      ),
  });

  // Update Explorer Auspiciadores category lock
  const updateExplorerSponsorCategoryMutation = useMutation({
    mutationFn: async (category: string | null) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyExplorerSponsorCategory(
        companyId,
        category,
      );
    },
    onSuccess: async (_, category) => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      setExplorerSponsorCategoryFilter(category || "");
      await refreshCompanyProducts();
      setSuccessMessage(
        category
          ? "Categoría de auspiciadores actualizada"
          : "Filtro de categoría de auspiciadores eliminado",
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          "Error actualizando categoría de auspiciadores",
      ),
  });

  const updateExplorerBrandInvestmentMutation = useMutation({
    mutationFn: async (payload: ExplorerBrandInvestment) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyExplorerBrandInvestment(
        companyId,
        payload,
      );
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      const inv = data.explorerBrandInvestment;
      setBrandInvestmentEnabled(Boolean(inv?.enabled));
      if (inv?.points) {
        setBrandInvestmentPoints(brandInvestmentPointsToForm(inv.points));
      }
      setBrandInvestmentClpPerPoint(
        inv?.clpPerPoint ? String(inv.clpPerPoint) : "",
      );
      await refreshCompanyProducts();
      setSuccessMessage(
        inv?.enabled
          ? "Estimación de inversión actualizada"
          : "Estimación de inversión desactivada",
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          "Error actualizando estimación de inversión",
      ),
  });

  const persistBrandInvestment = (enabled: boolean) => {
    const points: ExplorerBrandInvestmentPoints = {
      credits: Number(brandInvestmentPoints.credits),
      content: Number(brandInvestmentPoints.content),
      banner: Number(brandInvestmentPoints.banner),
      placement: Number(brandInvestmentPoints.placement),
    };
    const valid = Object.values(points).every(
      (n) => Number.isFinite(n) && n >= 0,
    );
    if (enabled && !valid) {
      setError("Los puntos deben ser números ≥ 0");
      return;
    }
    const clpText = brandInvestmentClpPerPoint.trim();
    const clpPerPoint = clpText === "" ? null : Number(clpText);
    if (
      enabled &&
      clpPerPoint != null &&
      !(Number.isFinite(clpPerPoint) && clpPerPoint > 0)
    ) {
      setError("El valor por punto debe ser un número > 0 o quedar vacío");
      return;
    }
    updateExplorerBrandInvestmentMutation.mutate({
      enabled,
      ...(valid ? { points } : {}),
      clpPerPoint,
    });
  };

  // Update comparador podcast IDs mutation
  const updateComparadorPodcastsMutation = useMutation({
    mutationFn: async (podcastIds: string[]) => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanyComparadorPodcasts(
        companyId,
        podcastIds,
      );
    },
    onSuccess: async (_, podcastIds) => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      setComparadorPodcastIds(podcastIds);
      setSuccessMessage("Podcasts del Comparador actualizados");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          "Error actualizando podcasts del Comparador",
      ),
  });

  const updateSidebarGuidesMutation = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("Company ID required");
      return await apiService.updateCompanySidebarGuides(
        companyId,
        sidebarGuidesUrl.trim() || null,
        sidebarGuidesLabel.trim() || null,
      );
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({
        queryKey: ["company-detail", companyId],
      });
      setSidebarGuidesUrl(data.sidebarGuidesUrl || "");
      setSidebarGuidesLabel(data.sidebarGuidesLabel || "");
      await refreshCompanyProducts();
      setSuccessMessage("Enlace de guías del sidebar actualizado");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          "Error actualizando enlace de guías del sidebar",
      ),
  });

  const savePipelinePlanMutation = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("Company ID required");
      const validScopes = pipelineScopes.filter((s) => !!s.podcastId);
      if (validScopes.length === 0) {
        throw new Error("Agrega al menos un podcast al plan");
      }
      return apiService.upsertCompanyContentPlan(companyId, {
        enabled: pipelinePlanEnabled,
        scopes: validScopes.map((s) => ({
          podcastId: s.podcastId,
          programIds:
            s.programIds && s.programIds.length > 0 ? s.programIds : undefined,
          brandIds:
            s.brandIds && s.brandIds.length > 0 ? s.brandIds : undefined,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["company-content-plan", companyId],
      });
      queryClient.invalidateQueries({ queryKey: ["companyContentPlans"] });
      setSuccessMessage("Configuración de pipeline guardada");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.error ||
          e?.message ||
          "Error guardando configuración de pipeline",
      ),
  });

  // Fetch tracker brand name for display when company has trackerBrandId
  const { data: trackerBrandData } = useQuery({
    queryKey: ["brand", companyTrackerBrandId],
    queryFn: () => apiService.getBrandData(companyTrackerBrandId!),
    enabled: !!companyTrackerBrandId && selectedProducts.includes("tracker"),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch brands for Tracker brand picker (search)
  const [debouncedTrackerBrandSearch, setDebouncedTrackerBrandSearch] =
    useState("");
  React.useEffect(() => {
    const t = setTimeout(
      () => setDebouncedTrackerBrandSearch(trackerBrandSearch),
      300,
    );
    return () => clearTimeout(t);
  }, [trackerBrandSearch]);
  const { data: trackerBrandsOptions } = useQuery({
    queryKey: ["brands-search", debouncedTrackerBrandSearch],
    queryFn: async () => {
      const res = await apiService.getBrands(
        1,
        30,
        debouncedTrackerBrandSearch || undefined,
        undefined,
        true,
        true,
      );
      return res.brands;
    },
    enabled:
      selectedProducts.includes("tracker") &&
      (trackerBrandDialogOpen || !companyTrackerBrandId),
    staleTime: 2 * 60 * 1000,
  });
  const trackerBrandsList = trackerBrandsOptions ?? [];

  // Fetch company users
  const { data: usersResp, refetch: refetchUsers } = useQuery({
    queryKey: ["company-users", companyId],
    queryFn: () => apiService.getCompanyUsers(companyId!),
    enabled: !!companyId,
  });
  const demoUsers = (usersResp?.users || []).filter((u) => u.isDemo);

  const {
    data: invitationsResp,
    refetch: refetchInvitations,
  } = useQuery({
    queryKey: ["company-user-invitations", companyId],
    queryFn: () => apiService.getCompanyUserInvitations(companyId!),
    enabled: !!companyId,
  });

  // Fetch admin users for dropdown
  const { data: adminUsersResp } = useQuery({
    queryKey: ["admin-users-dropdown"],
    queryFn: () => apiService.getAdminUsers(1, 200),
    enabled: !!companyId,
    staleTime: 60_000,
  });
  const adminUsers: User[] = adminUsersResp?.users || [];

  // Fetch company podcasts (with programs for admin entitlements)
  const { data: companyPodcastsResp } = useQuery({
    queryKey: ["company-podcasts-dropdown", companyId],
    queryFn: () =>
      apiService.getCompanyPodcasts(companyId!, { includePrograms: true }),
    enabled: !!companyId,
  });
  const companyPodcasts: Podcast[] =
    (companyPodcastsResp as any)?.podcasts || [];

  const companyPodcastIdsKey = React.useMemo(() => {
    const podcasts: Podcast[] =
      (companyPodcastsResp as any)?.podcasts || [];
    return podcasts
      .map((p) => p._id)
      .sort()
      .join(",");
  }, [companyPodcastsResp]);

  const {
    data: miContenidoPresentBrands,
    isLoading: miContenidoPresentBrandsLoading,
  } = useQuery({
    queryKey: ["mi-contenido-present-brands", companyId, companyPodcastIdsKey],
    queryFn: async () => {
      const podcasts: Podcast[] =
        (companyPodcastsResp as any)?.podcasts || [];
      const results = await Promise.all(
        podcasts.map((p) =>
          apiService.getPodcastPresentBrands(p._id, { days: 365 }),
        ),
      );
      const merged = new Map<
        string,
        { _id: string; name: string; mentionCount: number }
      >();
      for (const brands of results) {
        for (const b of brands) {
          const id = String(b._id);
          const prev = merged.get(id);
          merged.set(id, {
            _id: id,
            name: b.name,
            mentionCount: (prev?.mentionCount ?? 0) + (b.mentionCount ?? 0),
          });
        }
      }
      return Array.from(merged.values()).sort((a, b) =>
        a.name.localeCompare(b.name, "es"),
      );
    },
    enabled:
      !!companyId &&
      selectedProducts.includes("micontenido") &&
      companyPodcasts.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const { data: miContenidoSelectedBrandDetails } = useQuery({
    queryKey: ["mi-contenido-brands-selected", miContenidoBrandIds],
    queryFn: async () => {
      const results = await Promise.all(
        miContenidoBrandIds.map(async (id) => {
          try {
            const res = await apiService.getBrand(id);
            const b = res.brand ?? res.brands?.[0];
            return {
              _id: id,
              name: b?.name ?? id,
            };
          } catch {
            return { _id: id, name: id };
          }
        }),
      );
      return results;
    },
    enabled:
      miContenidoBrandIds.length > 0 &&
      selectedProducts.includes("micontenido"),
    staleTime: 5 * 60 * 1000,
  });

  const miContenidoBrandOptions = React.useMemo(() => {
    const merged = new Map<
      string,
      { _id: string; name: string; mentionCount?: number }
    >();
    for (const b of miContenidoPresentBrands ?? []) {
      merged.set(b._id, b);
    }
    for (const b of miContenidoSelectedBrandDetails ?? []) {
      if (!merged.has(b._id)) merged.set(b._id, b);
    }
    for (const id of miContenidoBrandIds) {
      if (!merged.has(id)) merged.set(id, { _id: id, name: id });
    }
    return Array.from(merged.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "es"),
    );
  }, [
    miContenidoPresentBrands,
    miContenidoSelectedBrandDetails,
    miContenidoBrandIds,
  ]);

  const miContenidoBrandSelected = React.useMemo(
    () =>
      miContenidoBrandOptions.filter((b) =>
        miContenidoBrandIds.includes(b._id),
      ),
    [miContenidoBrandOptions, miContenidoBrandIds],
  );

  // Fetch all livestream channels to find ones linked to this company's podcasts
  const { data: livestreamChannelsResp, refetch: refetchLivestreamChannels } =
    useQuery({
      queryKey: ["livestream-channels"],
      queryFn: () => apiService.getLivestreamChannels(),
    });
  // Index channels by their YouTube channelId so we can match against podcast.youtubeData.channelId
  const channelByYoutubeId = React.useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof livestreamChannelsResp>["channels"][number]
    >();
    for (const ch of livestreamChannelsResp?.channels ?? []) {
      map.set(ch.channelId, ch);
    }
    return map;
  }, [livestreamChannelsResp]);

  // Live schedule add-form state (keyed by channel._id)
  const [scheduleFormOpen, setScheduleFormOpen] = useState<
    Record<string, boolean>
  >({});
  const [scheduleFormData, setScheduleFormData] = useState<
    Record<
      string,
      {
        days: number[];
        times: string[];
      }
    >
  >({});

  const openScheduleForm = (channelId: string) => {
    setScheduleFormOpen((prev) => ({ ...prev, [channelId]: true }));
    setScheduleFormData((prev) => ({
      ...prev,
      [channelId]: prev[channelId] ?? { days: [], times: ["22:00"] },
    }));
  };

  const closeScheduleForm = (channelId: string) => {
    setScheduleFormOpen((prev) => ({ ...prev, [channelId]: false }));
  };

  const addScheduleMutation = useMutation({
    mutationFn: async ({
      channelId,
      days,
      times,
    }: {
      channelId: string;
      days: number[];
      times: string[];
    }) => {
      setError(null);
      for (const day of days) {
        for (const time of times) {
          const [hourStr, minuteStr] = time.split(":");
          const hour = parseInt(hourStr, 10);
          const minute = parseInt(minuteStr, 10);
          // Auto-generated unique key — not shown to the user
          const key = `slot_${day}_${hour.toString().padStart(2, "0")}${minute.toString().padStart(2, "0")}`;
          await apiService.addLivestreamChannelSchedule(channelId, {
            programName: key,
            dayOfWeek: day,
            hour,
            minute,
            timezone: "America/Santiago",
            isActive: true,
          });
        }
      }
    },
    onSuccess: (_data, variables) => {
      closeScheduleForm(variables.channelId);
      refetchLivestreamChannels();
      setSuccessMessage("Horarios agregados");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || "Error agregando horario"),
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: async ({
      channelId,
      programName,
    }: {
      channelId: string;
      programName: string;
    }) => {
      setError(null);
      await apiService.deleteLivestreamChannelSchedule(channelId, programName);
    },
    onSuccess: () => {
      refetchLivestreamChannels();
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || "Error eliminando horario"),
  });

  // Fetch assignable podcasts
  const { data: assignableResp, isLoading: assignableLoading } = useQuery({
    queryKey: ["assignable-podcasts", podcastSearch],
    queryFn: () =>
      apiService.getPodcastsForAssignment(1, 200, podcastSearch || undefined),
    enabled: !!companyId,
  });
  const assignablePodcasts: Podcast[] = (assignableResp as any)?.podcasts || [];

  // User mutations
  const addUserMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      if (!selectedUser) throw new Error("Selecciona un usuario");
      await apiService.addCompanyUser(companyId!, selectedUser._id, roleInput);
    },
    onSuccess: async () => {
      setSelectedUser(null);
      await refetchUsers();
      setSuccessMessage("Usuario agregado exitosamente");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || e?.message || "Error agregando usuario",
      ),
  });

  const inviteByEmailMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      const email = inviteEmail.trim().toLowerCase();
      if (!email) throw new Error("Ingresa un email");
      await apiService.inviteCompanyUserByEmail(companyId!, email, inviteRole);
    },
    onSuccess: async () => {
      setInviteEmail("");
      await refetchInvitations();
      setSuccessMessage("Invitación enviada por correo");
      setTimeout(() => setSuccessMessage(null), 4000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al enviar invitación",
      ),
  });

  const createInviteLinkMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      return apiService.createCompanyInviteLink(companyId!, linkInviteRole);
    },
    onSuccess: async (data) => {
      setGeneratedUrlKind("invite");
      setGeneratedInviteUrl(data.inviteUrl);
      setInviteUrlCopied(false);
      await refetchInvitations();
      setSuccessMessage("Enlace de invitación generado");
      setTimeout(() => setSuccessMessage(null), 4000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al generar enlace",
      ),
  });

  const createDemoUserMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      const email = demoEmail.trim().toLowerCase();
      if (!email) {
        throw new Error("Ingresa un email");
      }
      return apiService.createCompanyDemoUser(companyId!, {
        name: demoName.trim(),
        email,
        companyRole: demoRole,
      });
    },
    onSuccess: async (data) => {
      setDemoName("");
      setDemoEmail("");
      await refetchUsers();
      setSuccessMessage(
        `${
          data.created
            ? "Cuenta demo creada"
            : "Contraseña de la cuenta demo actualizada"
        } · Contraseña: ${data.password}`,
      );
      setTimeout(() => setSuccessMessage(null), 8000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al crear la cuenta demo",
      ),
  });

  const copyDemoLinkMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      return apiService.getCompanyDemoUserLink(companyId!, userId);
    },
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.loginUrl);
        setSuccessMessage("Enlace demo copiado");
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch {
        // Clipboard blocked: show the dialog so the admin can copy manually.
        setGeneratedUrlKind("demo");
        setGeneratedInviteUrl(data.loginUrl);
        setInviteUrlCopied(false);
      }
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al copiar el enlace demo",
      ),
  });

  const copyDemoPasswordMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      return apiService.getCompanyDemoUserPassword(companyId!, userId);
    },
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.password);
        setSuccessMessage("Contraseña copiada");
      } catch {
        setSuccessMessage(`Contraseña: ${data.password}`);
      }
      setTimeout(() => setSuccessMessage(null), 4000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al copiar la contraseña",
      ),
  });

  const deleteDemoUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      await apiService.deleteCompanyDemoUser(companyId!, userId);
    },
    onSuccess: async () => {
      setPendingDeleteDemoUser(null);
      await refetchUsers();
      setSuccessMessage("Cuenta demo eliminada");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al eliminar la cuenta demo",
      ),
  });

  const revokeDemoInviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      await apiService.revokeCompanyDemoUserInvite(companyId!, userId);
    },
    onSuccess: async () => {
      setPendingRevokeDemoInvite(null);
      await refetchUsers();
      setSuccessMessage("Invitación demo eliminada; el enlace anterior ya no funciona");
      setTimeout(() => setSuccessMessage(null), 4000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al eliminar la invitación demo",
      ),
  });

  const sendDemoInviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      return apiService.sendCompanyDemoUserInvite(companyId!, userId);
    },
    onSuccess: async (data) => {
      await refetchUsers();
      setSuccessMessage(`Invitación demo enviada a ${data.email}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al enviar la invitación demo",
      ),
  });

  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      await apiService.removeCompanyUser(companyId!, userId);
    },
    onSuccess: async () => {
      setDeleteUserDialogOpen(false);
      setPendingDeleteUser(null);
      await refetchUsers();
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSuccessMessage("Usuario eliminado de la empresa");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || "Error eliminando usuario"),
  });

  const copyInviteLinkMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      setError(null);
      return apiService.refreshCompanyInviteLink(companyId!, invitationId);
    },
    onSuccess: async (data) => {
      setGeneratedUrlKind("invite");
      setGeneratedInviteUrl(data.inviteUrl);
      setInviteUrlCopied(false);
      try {
        await navigator.clipboard.writeText(data.inviteUrl);
        setInviteUrlCopied(true);
      } catch {
        // Dialog stays open so the admin can copy manually.
      }
      await refetchInvitations();
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al copiar el enlace",
      ),
  });

  const deleteInviteMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      setError(null);
      await apiService.deleteCompanyUserInvitation(companyId!, invitationId);
    },
    onSuccess: async () => {
      setPendingDeleteInvite(null);
      await refetchInvitations();
      setSuccessMessage("Invitación eliminada");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al eliminar la invitación",
      ),
  });

  const updateCompanyUserRoleMutation = useMutation({
    mutationFn: async ({
      userId,
      companyRole,
    }: {
      userId: string;
      companyRole: "admin" | "member";
    }) => {
      setError(null);
      return apiService.updateCompanyUserRole(companyId!, userId, companyRole);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["company-users", companyId],
      });
      await refetchUsers();
      setSuccessMessage("Rol actualizado");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Error al actualizar el rol",
      ),
  });

  // Podcast mutations
  const addPodcastMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      if (!selectedPodcast) throw new Error("Selecciona un podcast");
      await apiService.addPodcastToCompany(companyId!, selectedPodcast._id);
    },
    onSuccess: async () => {
      setSelectedPodcast(null);
      await queryClient.invalidateQueries({
        queryKey: ["company-podcasts-dropdown", companyId],
      });
      setSuccessMessage("Podcast agregado exitosamente");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || e?.message || "Error agregando podcast",
      ),
  });

  const removePodcastMutation = useMutation({
    mutationFn: async (podcastId: string) => {
      setError(null);
      await apiService.removePodcastFromCompany(companyId!, podcastId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["company-podcasts-dropdown", companyId],
      });
      setSuccessMessage("Podcast eliminado exitosamente");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || "Error eliminando podcast"),
  });

  const updateSocialAccountsMutation = useMutation({
    mutationFn: async ({
      podcastId,
      instagram,
      tiktok,
      facebook,
    }: {
      podcastId: string;
      instagram: string;
      tiktok: string;
      facebook: string;
    }) => {
      setError(null);
      return await apiService.updatePodcastSocialAccounts(podcastId, {
        instagram: instagram.trim() || null,
        tiktok: tiktok.trim() || null,
        facebook: facebook.trim() || null,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["company-podcasts-dropdown", companyId],
      });
      setSocialAccountsDialog({
        open: false,
        podcast: null,
        instagram: "",
        tiktok: "",
        facebook: "",
      });
      setSuccessMessage("Cuentas sociales actualizadas exitosamente");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message || "Error actualizando cuentas sociales",
      ),
  });

  const updateProgramSettingsMutation = useMutation({
    mutationFn: async (payload: {
      programId: string;
      enabled?: boolean;
      livestreamDataEnabled?: boolean;
      demographicDataEnabled?: boolean;
    }) => {
      setError(null);
      return await apiService.updateCompanyProgramSettings(
        companyId!,
        payload.programId,
        {
          enabled: payload.enabled,
          livestreamDataEnabled: payload.livestreamDataEnabled,
          demographicDataEnabled: payload.demographicDataEnabled,
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["company-podcasts-dropdown", companyId],
      });
      await queryClient.invalidateQueries({ queryKey: ["collection-detail"] });
      setSuccessMessage("Programa actualizado");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || "Error actualizando programa"),
  });

  const deleteCompanyMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      if (!companyId) throw new Error("Company ID required");
      await apiService.deleteCompany(companyId);
    },
    onSuccess: async () => {
      setDeleteCompanyDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
      navigate("/admin");
    },
    onError: (e: any) =>
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "No se pudo eliminar la empresa",
      ),
  });

  const handleProductToggle = (productId: string) => {
    const newProducts = selectedProducts.includes(productId)
      ? selectedProducts.filter((p) => p !== productId)
      : [...selectedProducts, productId];
    setSelectedProducts(newProducts);
    updateProductsMutation.mutate(newProducts);
  };

  const handleOpenSocialAccountsDialog = (podcast: Podcast) => {
    setSocialAccountsDialog({
      open: true,
      podcast,
      instagram: podcast.socialAccounts?.instagram || "",
      tiktok: podcast.socialAccounts?.tiktok || "",
      facebook: podcast.socialAccounts?.facebook || "",
    });
  };

  const handleCloseSocialAccountsDialog = () => {
    setSocialAccountsDialog({
      open: false,
      podcast: null,
      instagram: "",
      tiktok: "",
      facebook: "",
    });
  };

  const handleSaveSocialAccounts = () => {
    if (!socialAccountsDialog.podcast) return;

    updateSocialAccountsMutation.mutate({
      podcastId: socialAccountsDialog.podcast._id,
      instagram: socialAccountsDialog.instagram,
      tiktok: socialAccountsDialog.tiktok,
      facebook: socialAccountsDialog.facebook,
    });
  };

  if (companyLoading) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (companyError || !company) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 3 }}>
          Error al cargar la empresa
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          mb={3}
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <IconButton onClick={() => navigate("/admin")}>
              <ArrowBackIcon />
            </IconButton>
            <CompanyImageEditor
              company={company}
              onSuccess={(message) => {
                setSuccessMessage(message);
                setTimeout(() => setSuccessMessage(null), 3000);
              }}
            />
            <Box>
              <Typography variant="h1">{company.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {company.slug}
              </Typography>
            </Box>
          </Stack>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteCompanyDialogOpen(true)}
          >
            Eliminar empresa
          </Button>
        </Stack>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {/* Product Access Configuration */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Acceso a Productos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {enablePerUserPermissions
              ? "Los productos seleccionados son los disponibles para asignar a usuarios individuales. Los usuarios heredan estos productos por defecto, pero puedes personalizar el acceso por usuario en la tabla de usuarios."
              : "Selecciona los productos a los que todos los usuarios de esta empresa tendrán acceso."}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={enablePerUserPermissions}
                onChange={(e) => {
                  setEnablePerUserPermissions(e.target.checked);
                  updatePerUserPermissionsMutation.mutate(e.target.checked);
                }}
                disabled={updatePerUserPermissionsMutation.isPending}
              />
            }
            label="Habilitar permisos por usuario"
            sx={{ mb: 2 }}
          />
          <Stack spacing={1}>
            {AVAILABLE_PRODUCTS.map((product) => {
              const Icon = product.icon;
              const isEnabled = selectedProducts.includes(product.id);
              return (
                <Paper
                  key={product.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    bgcolor: isEnabled ? "action.selected" : "transparent",
                    borderColor: isEnabled ? "primary.main" : "divider",
                    "&:hover": { bgcolor: "action.hover" },
                    transition: "all 0.2s",
                  }}
                  onClick={() => handleProductToggle(product.id)}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Checkbox
                      checked={isEnabled}
                      disabled={updateProductsMutation.isPending}
                      sx={{ p: 0 }}
                    />
                    <Icon color={isEnabled ? "primary" : "action"} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {product.description}
                      </Typography>
                    </Box>
                    {isEnabled && (
                      <Chip label="Activo" color="primary" size="small" />
                    )}
                  </Stack>
                </Paper>
              );
            })}
            {companyId && (
              <ChatProductToggle
                companyId={companyId}
                icon={<ChatBubbleOutlineIcon color="action" />}
              />
            )}
          </Stack>
          {updateProductsMutation.isPending && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Actualizando...
              </Typography>
            </Box>
          )}
          {(selectedProducts.includes("explorer") ||
            selectedProducts.includes("tracker") ||
            EXPLORER_TAB_PRODUCTS.some((p) =>
              selectedProducts.includes(p.id),
            )) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">Filtro de fechas:</Typography>
                {updateCompanyDateFilterMutation.isPending && (
                  <CircularProgress size={16} />
                )}
              </Stack>
              <ClearableDateField
                label="Desde"
                value={companyDateFilter}
                clearTitle="Quitar (usar fecha mínima por defecto)"
                onChange={(val) => {
                  setCompanyDateFilter(val);
                  dateFilterDraftRef.current = {
                    min: val,
                    max: companyDateMaxFilter,
                  };
                  scheduleDateFilterPersist();
                }}
              />
              <ClearableDateField
                label="Hasta"
                value={companyDateMaxFilter}
                clearTitle="Quitar (acceso hasta hoy)"
                onChange={(val) => {
                  setCompanyDateMaxFilter(val);
                  dateFilterDraftRef.current = {
                    min: companyDateFilter,
                    max: val,
                  };
                  scheduleDateFilterPersist();
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Vacío = sin tope (acceso hasta hoy)
              </Typography>
            </Stack>
          )}
          {(selectedProducts.includes("explorer") ||
            selectedProducts.includes("explorer-sponsors")) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="body2">
                Categoría de auspiciadores:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel shrink id="explorer-sponsor-category-label">
                  Categoría
                </InputLabel>
                <Select
                  labelId="explorer-sponsor-category-label"
                  label="Categoría"
                  value={explorerSponsorCategoryFilter}
                  displayEmpty
                  disabled={updateExplorerSponsorCategoryMutation.isPending}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    setExplorerSponsorCategoryFilter(val);
                    updateExplorerSponsorCategoryMutation.mutate(
                      val || null,
                    );
                  }}
                  renderValue={(selected) =>
                    selected
                      ? BRAND_CATEGORY_LABELS[selected] || selected
                      : "Todas"
                  }
                >
                  <MenuItem value="">Todas</MenuItem>
                  {Object.entries(BRAND_CATEGORY_LABELS)
                    .sort(([, a], [, b]) => a.localeCompare(b))
                    .map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {updateExplorerSponsorCategoryMutation.isPending && (
                <CircularProgress size={16} />
              )}
            </Stack>
          )}
          {(selectedProducts.includes("explorer") ||
            selectedProducts.includes("explorer-sponsors") ||
            selectedProducts.includes("explorer-programs")) && (
            <Box sx={{ mt: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                flexWrap="wrap"
                useFlexGap
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={brandInvestmentEnabled}
                      disabled={updateExplorerBrandInvestmentMutation.isPending}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setBrandInvestmentEnabled(enabled);
                        persistBrandInvestment(enabled);
                      }}
                    />
                  }
                  label="Estimación de inversión"
                />
                {updateExplorerBrandInvestmentMutation.isPending && (
                  <CircularProgress size={16} />
                )}
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, mb: 1.5 }}
              >
                Puntos por tipo de mención. Por cada marca y episodio se suman
                los puntos de cada tipo presente (una vez por tipo).
              </Typography>
              {brandInvestmentEnabled && (
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    spacing={2}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="flex-start"
                  >
                    {BRAND_INVESTMENT_POINT_FIELDS.map((field) => (
                      <TextField
                        key={field.key}
                        size="small"
                        label={`${field.label} (pts)`}
                        type="number"
                        inputProps={{ min: 0 }}
                        value={brandInvestmentPoints[field.key]}
                        onChange={(e) =>
                          setBrandInvestmentPoints((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        sx={{ width: 180 }}
                        helperText={field.helperText}
                      />
                    ))}
                    <TextField
                      size="small"
                      label="Valor por punto (CLP)"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={brandInvestmentClpPerPoint}
                      onChange={(e) =>
                        setBrandInvestmentClpPerPoint(e.target.value)
                      }
                      sx={{ width: 180 }}
                      helperText="Vacío = mostrar solo puntos"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={updateExplorerBrandInvestmentMutation.isPending}
                      onClick={() => persistBrandInvestment(true)}
                      sx={{ alignSelf: "center" }}
                    >
                      Guardar puntos
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          )}
          {selectedProducts.includes("micontenido") && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">
                  Límites del selector (Mi contenido):
                </Typography>
                {updateCompanyMiContenidoDateFilterMutation.isPending && (
                  <CircularProgress size={16} />
                )}
              </Stack>
              <ClearableDateField
                label="Desde"
                value={miContenidoDateFilterMin}
                clearTitle="Quitar (usar fecha mínima por defecto)"
                onChange={(val) => {
                  setMiContenidoDateFilterMin(val);
                  miContenidoDateFilterDraftRef.current = {
                    min: val,
                    max: miContenidoDateFilterMax,
                  };
                  scheduleMiContenidoDateFilterPersist();
                }}
              />
              <ClearableDateField
                label="Hasta"
                value={miContenidoDateFilterMax}
                clearTitle="Quitar (acceso hasta hoy)"
                onChange={(val) => {
                  setMiContenidoDateFilterMax(val);
                  miContenidoDateFilterDraftRef.current = {
                    min: miContenidoDateFilterMin,
                    max: val,
                  };
                  scheduleMiContenidoDateFilterPersist();
                }}
              />
            </Stack>
          )}
          {selectedProducts.includes("micontenido") && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">
                  Marcas permitidas (Mi contenido):
                </Typography>
                {updateMiContenidoBrandsMutation.isPending && (
                  <CircularProgress size={16} />
                )}
              </Stack>
              <Autocomplete
                multiple
                size="small"
                sx={{ maxWidth: 560 }}
                options={miContenidoBrandOptions}
                getOptionLabel={(opt) => opt.name}
                value={miContenidoBrandSelected}
                onChange={(_, v) => {
                  const ids = v.map((b) => b._id);
                  setMiContenidoBrandIds(ids);
                  updateMiContenidoBrandsMutation.mutate(ids);
                }}
                loading={miContenidoPresentBrandsLoading}
                disabled={updateMiContenidoBrandsMutation.isPending}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                noOptionsText={
                  miContenidoPresentBrandsLoading
                    ? "Cargando marcas del canal…"
                    : companyPodcasts.length === 0
                      ? "Asigna podcasts a la empresa para ver marcas"
                      : "No hay menciones en los canales (últimos 365 días)"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Marcas del canal"
                    placeholder="Vacío = todas las marcas"
                    helperText="Opciones: marcas con menciones en los podcasts de la empresa. Si seleccionas, los usuarios solo verán esas en Mi Contenido."
                  />
                )}
                renderOption={(props, opt) => (
                  <li {...props} key={opt._id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {opt.mentionCount != null && opt.mentionCount > 0
                          ? `${opt.name} (${opt.mentionCount} menc.)`
                          : opt.name}
                      </Typography>
                    </Stack>
                  </li>
                )}
              />
            </Stack>
          )}
          {selectedProducts.includes("tracker") && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="body2">Marca del Tracker:</Typography>
              {companyTrackerBrandId ? (
                <>
                  <Chip
                    label={
                      trackerBrandData?.brand?.name ?? companyTrackerBrandId
                    }
                    size="small"
                    onDelete={
                      updateTrackerBrandMutation.isPending
                        ? undefined
                        : () => updateTrackerBrandMutation.mutate(null)
                    }
                    sx={{ fontWeight: 500 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setTrackerBrandPick(
                        trackerBrandData?.brand
                          ? {
                              _id: trackerBrandData.brand._id,
                              name: trackerBrandData.brand.name,
                            }
                          : null,
                      );
                      setTrackerBrandSearch(
                        trackerBrandData?.brand?.name ?? "",
                      );
                      setTrackerBrandDialogOpen(true);
                    }}
                  >
                    Cambiar
                  </Button>
                </>
              ) : (
                <>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 260 }}
                    options={trackerBrandsList}
                    getOptionLabel={(opt) => opt.name}
                    value={trackerBrandPick}
                    onChange={(_, v) => setTrackerBrandPick(v)}
                    onInputChange={(_, v) => setTrackerBrandSearch(v)}
                    filterOptions={(x) => x}
                    loading={
                      !trackerBrandsList.length && !!debouncedTrackerBrandSearch
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar marca"
                        placeholder="Nombre de la marca..."
                      />
                    )}
                    renderOption={(props, opt) => (
                      <li {...props} key={opt._id}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{opt.name}</Typography>
                        </Stack>
                      </li>
                    )}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={
                      !trackerBrandPick || updateTrackerBrandMutation.isPending
                    }
                    onClick={() => {
                      if (trackerBrandPick) {
                        updateTrackerBrandMutation.mutate(trackerBrandPick._id);
                      }
                    }}
                  >
                    {updateTrackerBrandMutation.isPending
                      ? "Guardando..."
                      : "Guardar"}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Paper>

        {companyId && <CompanyPermissionsPanel companyId={companyId} />}

        {companyId && <CompanyChatProfilePanel companyId={companyId} />}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Guías del sidebar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enlace externo que ven los usuarios de esta empresa en el sidebar
            (por defecto &quot;{productBrand.guidesLabel}&quot;). Deja la URL vacía para
            usar el enlace global por defecto.
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Etiqueta del enlace"
              placeholder={productBrand.guidesLabel}
              value={sidebarGuidesLabel}
              onChange={(e) => setSidebarGuidesLabel(e.target.value)}
              helperText='Texto del botón en el sidebar (ej. "Manuales")'
            />
            <TextField
              fullWidth
              size="small"
              label="URL"
              placeholder="https://..."
              value={sidebarGuidesUrl}
              onChange={(e) => setSidebarGuidesUrl(e.target.value)}
              helperText="Notion u otra URL pública. Debe comenzar con http:// o https://"
            />
            <Box>
              <Button
                variant="contained"
                disabled={updateSidebarGuidesMutation.isPending}
                onClick={() => updateSidebarGuidesMutation.mutate()}
              >
                {updateSidebarGuidesMutation.isPending
                  ? "Guardando..."
                  : "Guardar guías"}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Dialog
          open={deleteCompanyDialogOpen}
          onClose={() =>
            !deleteCompanyMutation.isPending &&
            setDeleteCompanyDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Eliminar empresa</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Esta acción no se puede deshacer. Se eliminará la empresa{" "}
              <strong>{company.name}</strong>, las cuentas de usuario asociadas
              a esta empresa, los vínculos con podcasts, datos de uso,
              invitaciones, cuentas sociales enlazadas y contenido privado
              (colecciones, grupos, etc.) pertenecientes solo a esta empresa.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteCompanyDialogOpen(false)}
              disabled={deleteCompanyMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteCompanyMutation.isPending}
              onClick={() => deleteCompanyMutation.mutate()}
            >
              {deleteCompanyMutation.isPending
                ? "Eliminando…"
                : "Eliminar definitivamente"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteUserDialogOpen}
          onClose={() =>
            !removeUserMutation.isPending && setDeleteUserDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Eliminar usuario de la empresa</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {pendingDeleteUser?.isDemo ? (
                <>
                  Se eliminará la cuenta demo de{" "}
                  <strong>{pendingDeleteUser.name}</strong> (
                  {pendingDeleteUser.email}). El email quedará libre para crear
                  otra cuenta.
                </>
              ) : (
                <>
                  Se quitará a <strong>{pendingDeleteUser?.name}</strong> (
                  {pendingDeleteUser?.email}) de esta empresa. La cuenta no se
                  elimina; podrás volver a agregarla después.
                </>
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteUserDialogOpen(false)}
              disabled={removeUserMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={removeUserMutation.isPending || !pendingDeleteUser}
              onClick={() =>
                pendingDeleteUser &&
                removeUserMutation.mutate(pendingDeleteUser._id)
              }
            >
              {removeUserMutation.isPending
                ? "Eliminando…"
                : "Quitar de la empresa"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!pendingDeleteInvite}
          onClose={() =>
            !deleteInviteMutation.isPending && setPendingDeleteInvite(null)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Eliminar invitación</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Se eliminará la invitación de{" "}
              <strong>{pendingDeleteInvite?.label}</strong>. El enlace dejará
              de funcionar.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPendingDeleteInvite(null)}
              disabled={deleteInviteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteInviteMutation.isPending || !pendingDeleteInvite}
              onClick={() =>
                pendingDeleteInvite &&
                deleteInviteMutation.mutate(pendingDeleteInvite.id)
              }
            >
              {deleteInviteMutation.isPending ? "Eliminando…" : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={userFiltersDialogOpen}
          onClose={() =>
            !updateUserFiltersMutation.isPending &&
            setUserFiltersDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Filtros de {userFiltersTarget?.name || "usuario"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Deja vacío para heredar la configuración de la empresa. Los
              valores aquí tienen prioridad sobre el filtro de la empresa.
            </Typography>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Filtro de fechas (Explorador)
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <ClearableDateField
                    label="Desde"
                    value={userFilterDateMin}
                    clearTitle="Quitar (usar empresa)"
                    onChange={setUserFilterDateMin}
                    helperText={
                      companyDateFilter
                        ? `Empresa: ${companyDateFilter}`
                        : "Sin filtro de empresa"
                    }
                  />
                  <ClearableDateField
                    label="Hasta"
                    value={userFilterDateMax}
                    clearTitle="Quitar (usar empresa)"
                    onChange={setUserFilterDateMax}
                    helperText={
                      companyDateMaxFilter
                        ? `Empresa: ${companyDateMaxFilter}`
                        : "Sin filtro de empresa"
                    }
                  />
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Categoría de auspiciadores
                </Typography>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel shrink id="user-sponsor-category-label">
                    Categoría
                  </InputLabel>
                  <Select
                    labelId="user-sponsor-category-label"
                    label="Categoría"
                    value={userFilterSponsorCategory}
                    displayEmpty
                    onChange={(e) =>
                      setUserFilterSponsorCategory(e.target.value as string)
                    }
                    renderValue={(selected) => {
                      if (selected === "__inherit__") {
                        return explorerSponsorCategoryFilter
                          ? `Empresa: ${BRAND_CATEGORY_LABELS[explorerSponsorCategoryFilter] || explorerSponsorCategoryFilter}`
                          : "Usar empresa (Todas)";
                      }
                      if (selected === "") return "Todas (forzar)";
                      return BRAND_CATEGORY_LABELS[selected] || selected;
                    }}
                  >
                    <MenuItem value="__inherit__">
                      Usar configuración de la empresa
                    </MenuItem>
                    <MenuItem value="">Todas (forzar)</MenuItem>
                    {Object.entries(BRAND_CATEGORY_LABELS)
                      .sort(([, a], [, b]) => a.localeCompare(b))
                      .map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Filtro de fechas (Mi contenido)
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <ClearableDateField
                    label="Desde"
                    value={userFilterMiMin}
                    clearTitle="Quitar (usar empresa)"
                    onChange={setUserFilterMiMin}
                    helperText={
                      miContenidoDateFilterMin
                        ? `Empresa: ${miContenidoDateFilterMin}`
                        : "Sin filtro de empresa"
                    }
                  />
                  <ClearableDateField
                    label="Hasta"
                    value={userFilterMiMax}
                    clearTitle="Quitar (usar empresa)"
                    onChange={setUserFilterMiMax}
                    helperText={
                      miContenidoDateFilterMax
                        ? `Empresa: ${miContenidoDateFilterMax}`
                        : "Sin filtro de empresa"
                    }
                  />
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setUserFiltersDialogOpen(false)}
              disabled={updateUserFiltersMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={
                updateUserFiltersMutation.isPending || !userFiltersTarget
              }
              onClick={() => updateUserFiltersMutation.mutate()}
            >
              {updateUserFiltersMutation.isPending
                ? "Guardando…"
                : "Guardar filtros"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: change Tracker brand */}
        <Dialog
          open={trackerBrandDialogOpen}
          onClose={() => setTrackerBrandDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Cambiar marca del Tracker</DialogTitle>
          <DialogContent>
            <Autocomplete
              size="small"
              sx={{ mt: 1, minWidth: 260 }}
              options={trackerBrandsList}
              getOptionLabel={(opt) => opt.name}
              value={trackerBrandPick}
              onChange={(_, v) => setTrackerBrandPick(v)}
              onInputChange={(_, v) => setTrackerBrandSearch(v)}
              filterOptions={(x) => x}
              loading={
                !trackerBrandsList.length && !!debouncedTrackerBrandSearch
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar marca"
                  placeholder="Nombre de la marca..."
                />
              )}
              renderOption={(props, opt) => (
                <li {...props} key={opt._id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">{opt.name}</Typography>
                  </Stack>
                </li>
              )}
            />
          </DialogContent>
          <DialogActions
            sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
          >
            <Button
              color="error"
              disabled={
                !companyTrackerBrandId || updateTrackerBrandMutation.isPending
              }
              onClick={() => updateTrackerBrandMutation.mutate(null)}
            >
              {updateTrackerBrandMutation.isPending
                ? "Guardando..."
                : "Quitar marca"}
            </Button>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setTrackerBrandDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                disabled={
                  !trackerBrandPick || updateTrackerBrandMutation.isPending
                }
                onClick={() => {
                  if (trackerBrandPick) {
                    updateTrackerBrandMutation.mutate(trackerBrandPick._id);
                  }
                }}
              >
                {updateTrackerBrandMutation.isPending
                  ? "Guardando..."
                  : "Guardar"}
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>

        {/* Comparador — Podcast IDs */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Comparador — Podcasts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona los podcasts que aparecerán en la sección Comparador para
            esta empresa. El producto "Comparador" debe estar habilitado arriba
            para que los usuarios puedan acceder.
          </Typography>
          <Stack spacing={1.5}>
            <Autocomplete
              multiple
              size="small"
              sx={{ maxWidth: 600 }}
              options={assignablePodcasts}
              getOptionLabel={(opt) => opt.title}
              value={assignablePodcasts.filter((p) =>
                comparadorPodcastIds.includes(p._id),
              )}
              onChange={(_, selected) => {
                setComparadorPodcastIds(selected.map((p) => p._id));
              }}
              loading={assignableLoading}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar podcasts"
                  placeholder="Buscar podcast..."
                />
              )}
              renderOption={(props, opt) => (
                <li {...props} key={opt._id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PodcastsIcon fontSize="small" color="action" />
                    <Typography variant="body2">{opt.title}</Typography>
                  </Stack>
                </li>
              )}
            />
            {comparadorPodcastIds.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {comparadorPodcastIds.length} podcast
                {comparadorPodcastIds.length !== 1 ? "s" : ""} seleccionado
                {comparadorPodcastIds.length !== 1 ? "s" : ""}
              </Typography>
            )}
            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={updateComparadorPodcastsMutation.isPending}
                onClick={() =>
                  updateComparadorPodcastsMutation.mutate(comparadorPodcastIds)
                }
              >
                {updateComparadorPodcastsMutation.isPending
                  ? "Guardando..."
                  : "Guardar podcasts"}
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* User Management */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Gestión de Usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Usa el ícono de filtros para sobreescribir
            fechas / categoría por usuario; vacío = hereda la configuración de
            la empresa.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Add User */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Invitar por email (el usuario recibirá un enlace para crear su
              cuenta)
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mb: 3 }}
            >
              <Box sx={{ position: "relative", flex: 1, minWidth: 220 }}>
                <TextField
                  label="Email"
                  type="email"
                  size="small"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  sx={{ width: "100%" }}
                  placeholder="correo@empresa.com"
                  disabled={isLocalhost}
                />
                {isLocalhost && (
                  <Box
                    onClick={() => setLocalhostInviteSnackbarOpen(true)}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      cursor: "pointer",
                      zIndex: 1,
                      borderRadius: 1,
                    }}
                    aria-hidden
                  />
                )}
              </Box>
              <TextField
                select
                label="Rol"
                value={inviteRole}
                onChange={(e) =>
                  setInviteRole(e.target.value as "admin" | "member")
                }
                size="small"
                sx={{ width: 120 }}
                SelectProps={{ native: true }}
                disabled={isLocalhost}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
              </TextField>
              <Button
                variant="outlined"
                onClick={() => inviteByEmailMutation.mutate()}
                disabled={
                  inviteByEmailMutation.isPending ||
                  !inviteEmail.trim() ||
                  isLocalhost
                }
              >
                {inviteByEmailMutation.isPending
                  ? "Enviando…"
                  : "Enviar invitación"}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generar enlace de invitación (de un solo uso; cópialo y
              compártelo)
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mb: 3 }}
            >
              <TextField
                select
                label="Rol"
                value={linkInviteRole}
                onChange={(e) =>
                  setLinkInviteRole(e.target.value as "admin" | "member")
                }
                size="small"
                sx={{ width: 120 }}
                SelectProps={{ native: true }}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
              </TextField>
              <Button
                variant="outlined"
                onClick={() => createInviteLinkMutation.mutate()}
                disabled={createInviteLinkMutation.isPending}
              >
                {createInviteLinkMutation.isPending
                  ? "Generando…"
                  : "Generar enlace"}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Crear cuenta demo (la contraseña se genera automáticamente como
              nombre-empresa-número; no se envía nada todavía). Desde la lista
              de cuentas demo puedes enviar la invitación por email, copiar el
              enlace con el login prellenado o copiar la contraseña. Si el email
              ya es una cuenta demo de esta empresa (o una demo que se quitó
              de la empresa), se genera una nueva contraseña y el enlace
              anterior deja de funcionar.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mb: 3 }}
            >
              <TextField
                label="Nombre"
                size="small"
                value={demoName}
                onChange={(e) => setDemoName(e.target.value)}
                placeholder="Cuenta demo"
                sx={{ flex: 1, minWidth: 160 }}
              />
              <TextField
                label="Email"
                type="email"
                size="small"
                value={demoEmail}
                onChange={(e) => setDemoEmail(e.target.value)}
                placeholder="demo@empresa.com"
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                select
                label="Rol"
                value={demoRole}
                onChange={(e) =>
                  setDemoRole(e.target.value as "admin" | "member")
                }
                size="small"
                sx={{ width: 120 }}
                SelectProps={{ native: true }}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
              </TextField>
              <Button
                variant="outlined"
                onClick={() => createDemoUserMutation.mutate()}
                disabled={
                  createDemoUserMutation.isPending || !demoEmail.trim()
                }
              >
                {createDemoUserMutation.isPending
                  ? "Creando…"
                  : "Crear cuenta demo"}
              </Button>
            </Stack>

            <Typography variant="subtitle1" gutterBottom>
              Cuentas demo
            </Typography>
            <TableContainer
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                mb: 3,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Invitación</TableCell>
                    <TableCell>Último login</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {demoUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No hay cuentas demo aún
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    demoUsers.map((u) => (
                      <TableRow key={u._id}>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.companyRole || "member"}</TableCell>
                        <TableCell>
                          {u.demoInviteSentAt ? (
                            <Tooltip
                              title={new Date(
                                u.demoInviteSentAt,
                              ).toLocaleString()}
                            >
                              <Chip size="small" color="success" label="Enviada" />
                            </Tooltip>
                          ) : (
                            <Chip size="small" color="warning" label="Pendiente" />
                          )}
                        </TableCell>
                        <TableCell>
                          {u.lastLogin
                            ? new Date(u.lastLogin).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip
                            title={
                              isLocalhost
                                ? "El envío de emails no está disponible en localhost"
                                : "Enviar invitación por email"
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<SendIcon fontSize="small" />}
                                onClick={() =>
                                  sendDemoInviteMutation.mutate(u._id)
                                }
                                disabled={
                                  isLocalhost ||
                                  (sendDemoInviteMutation.isPending &&
                                    sendDemoInviteMutation.variables === u._id)
                                }
                                sx={{ mr: 1 }}
                              >
                                {sendDemoInviteMutation.isPending &&
                                sendDemoInviteMutation.variables === u._id
                                  ? "Enviando…"
                                  : "Enviar invitación"}
                              </Button>
                            </span>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            onClick={() => copyDemoLinkMutation.mutate(u._id)}
                            disabled={
                              copyDemoLinkMutation.isPending &&
                              copyDemoLinkMutation.variables === u._id
                            }
                          >
                            Copiar enlace
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            onClick={() =>
                              copyDemoPasswordMutation.mutate(u._id)
                            }
                            disabled={
                              copyDemoPasswordMutation.isPending &&
                              copyDemoPasswordMutation.variables === u._id
                            }
                            sx={{ ml: 1 }}
                          >
                            Copiar contraseña
                          </Button>
                          <Tooltip title="Eliminar invitación (invalida el enlace enviado o copiado)">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() =>
                                setPendingRevokeDemoInvite({
                                  _id: u._id,
                                  email: u.email,
                                })
                              }
                              aria-label={`Eliminar invitación demo ${u.email}`}
                              sx={{ ml: 1 }}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar cuenta demo">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setPendingDeleteDemoUser({
                                  _id: u._id,
                                  email: u.email,
                                })
                              }
                              aria-label={`Eliminar cuenta demo ${u.email}`}
                              sx={{ ml: 1 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Agregar usuarios existentes a esta empresa
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Autocomplete
                disablePortal
                options={adminUsers}
                getOptionLabel={(option: User) =>
                  `${option.name} <${option.email}>`
                }
                value={selectedUser}
                onChange={(_e, value) => setSelectedUser(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Seleccionar usuario"
                    size="small"
                  />
                )}
                sx={{ flex: 1 }}
              />
              <TextField
                select
                label="Rol"
                value={roleInput}
                onChange={(e) =>
                  setRoleInput(e.target.value as "admin" | "member")
                }
                size="small"
                sx={{ width: 120 }}
                SelectProps={{ native: true }}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
              </TextField>
              <Button
                variant="contained"
                onClick={() => addUserMutation.mutate()}
                disabled={addUserMutation.isPending || !selectedUser}
              >
                Añadir
              </Button>
            </Stack>
          </Box>

          {/* Invitations status */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Invitaciones
            </Typography>
            <TableContainer
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invitationsResp?.invitations || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No hay invitaciones aún
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (invitationsResp?.invitations || []).map((inv) => {
                      const statusLabel =
                        inv.status === "accepted"
                          ? "Aceptado"
                          : inv.status === "expired"
                            ? "Expirado"
                            : inv.status === "revoked"
                              ? "Revocado"
                              : "Invitado";
                      const statusColor =
                        inv.status === "accepted"
                          ? "success"
                          : inv.status === "expired" ||
                              inv.status === "revoked"
                            ? "default"
                            : "warning";
                      return (
                        <TableRow key={inv.id}>
                          <TableCell>
                            {inv.email || (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                              >
                                Enlace
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {inv.kind === "link" ? "Enlace" : "Email"}
                          </TableCell>
                          <TableCell>{inv.companyRole}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={statusLabel}
                              color={statusColor as "success" | "warning" | "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {inv.createdAt
                              ? new Date(inv.createdAt).toLocaleString()
                              : "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {(inv.status === "pending" ||
                              inv.status === "expired") && (
                              <Tooltip title="Copiar enlace">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      copyInviteLinkMutation.mutate(inv.id)
                                    }
                                    disabled={copyInviteLinkMutation.isPending}
                                    aria-label="Copiar enlace de invitación"
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Eliminar invitación">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    setPendingDeleteInvite({
                                      id: inv.id,
                                      label: inv.email || "enlace",
                                    })
                                  }
                                  disabled={deleteInviteMutation.isPending}
                                  aria-label="Eliminar invitación"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Users Table — many product columns; horizontal scroll to reach all (e.g. Mi contenido) */}
          <TableContainer
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table
              size="small"
              sx={{ minWidth: enablePerUserPermissions ? 1200 : undefined }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rol</TableCell>
                  {enablePerUserPermissions && (
                    <>
                      {AVAILABLE_PRODUCTS.map((product) => (
                        <TableCell
                          key={product.id}
                          align="center"
                          sx={{ minWidth: 120 }}
                        >
                          {product.name}
                        </TableCell>
                      ))}
                    </>
                  )}
                  <TableCell>Último login</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      position: "sticky",
                      right: 0,
                      bgcolor: "background.paper",
                      zIndex: 2,
                      boxShadow: "-8px 0 8px -8px rgba(0,0,0,0.2)",
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(usersResp?.users || []).map((u) => {
                  const userProducts = u.userProducts || [];
                  return (
                    <TableRow key={u._id}>
                      <TableCell>
                        {u.name}
                        {u.isDemo && (
                          <Chip
                            label="Demo"
                            size="small"
                            color="info"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          select
                          size="small"
                          value={
                            u.companyRole === "admin" ||
                            u.companyRole === "member"
                              ? u.companyRole
                              : "member"
                          }
                          onChange={(e) => {
                            const next = e.target.value as "admin" | "member";
                            if (next === u.companyRole) return;
                            updateCompanyUserRoleMutation.mutate({
                              userId: u._id,
                              companyRole: next,
                            });
                          }}
                          disabled={
                            updateCompanyUserRoleMutation.isPending ||
                            (authUser != null &&
                              String(authUser._id) === String(u._id))
                          }
                          SelectProps={{ native: true }}
                          sx={{ minWidth: 108 }}
                        >
                          <option value="member">member</option>
                          <option value="admin">admin</option>
                        </TextField>
                      </TableCell>
                      {enablePerUserPermissions && (
                        <>
                          {AVAILABLE_PRODUCTS.map((product) => {
                            const hasAccess = userProducts.includes(product.id);
                            const companyOffersProduct =
                              selectedProducts.includes(product.id);
                            // Block granting products the company does not offer; always allow
                            // revoking (e.g. clear stale `explorer` when company only has tabs).
                            const disabledGrantOnly =
                              updateUserProductsMutation.isPending ||
                              (!hasAccess && !companyOffersProduct);
                            return (
                              <TableCell key={product.id} align="center">
                                <Checkbox
                                  checked={hasAccess}
                                  disabled={disabledGrantOnly}
                                  onChange={(e) => {
                                    const newProducts = e.target.checked
                                      ? [...userProducts, product.id]
                                      : userProducts.filter(
                                          (p) => p !== product.id,
                                        );
                                    updateUserProductsMutation.mutate({
                                      userId: u._id,
                                      products: newProducts,
                                    });
                                  }}
                                  size="small"
                                />
                              </TableCell>
                            );
                          })}
                        </>
                      )}
                      <TableCell>
                        {u.lastLogin
                          ? new Date(u.lastLogin).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          position: "sticky",
                          right: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                          whiteSpace: "nowrap",
                          boxShadow: "-8px 0 8px -8px rgba(0,0,0,0.2)",
                        }}
                      >
                        <Tooltip title="Filtros por usuario (fechas / categoría)">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openUserFiltersDialog(u)}
                            aria-label={`Filtros de ${u.name}`}
                          >
                            <TuneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            authUser != null &&
                            String(authUser._id) === String(u._id)
                              ? "No puedes quitarte a ti mismo"
                              : "Quitar de la empresa"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setPendingDeleteUser({
                                  _id: u._id,
                                  name: u.name,
                                  email: u.email,
                                  isDemo: u.isDemo,
                                });
                                setDeleteUserDialogOpen(true);
                              }}
                              disabled={
                                removeUserMutation.isPending ||
                                (authUser != null &&
                                  String(authUser._id) === String(u._id))
                              }
                              aria-label={`Quitar a ${u.name} de la empresa`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Pipeline configuration */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Configuración pipeline
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define qué podcasts, programas y marcas procesan{" "}
            <code>company:mentions:pipeline</code>,{" "}
            <code>company:mentions:plans</code> y{" "}
            <code>company:short:plans</code>. Puedes elegir cualquier podcast
            del catálogo (no hace falta asignarlo antes en &quot;Podcasts de la
            Empresa&quot;). Los toggles &quot;Habilitado&quot; por programa
            controlan acceso al producto, no este alcance.
            Datos sociales: OAuth cuando hay cuenta conectada; si no, el
            pipeline corto usa Apify cuando el podcast tiene handles IG/TikTok/Facebook.
          </Typography>

          {contentPlanLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={pipelinePlanEnabled}
                    onChange={(e) => setPipelinePlanEnabled(e.target.checked)}
                  />
                }
                label="Plan habilitado (incluido en ejecuciones CLI por defecto)"
              />

              {companyId && (
                <ContentPlanScopesEditor
                  companyId={companyId}
                  scopes={pipelineScopes}
                  onChange={setPipelineScopes}
                  showBrands
                  showSocialStatus
                  includeAssignablePodcasts
                  disabled={savePipelinePlanMutation.isPending}
                />
              )}

              <Box>
                <Button
                  variant="contained"
                  onClick={() => savePipelinePlanMutation.mutate()}
                  disabled={
                    savePipelinePlanMutation.isPending ||
                    pipelineScopes.filter((s) => !!s.podcastId).length === 0
                  }
                >
                  {savePipelinePlanMutation.isPending
                    ? "Guardando..."
                    : "Guardar configuración pipeline"}
                </Button>
              </Box>
            </Stack>
          )}
        </Paper>

        {/* Podcast Management */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Podcasts de la Empresa
          </Typography>

          {/* Add Podcast */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Asignar podcasts a esta empresa
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                Company ID: {companyId}
              </Typography>
              {companyId && (
                <Tooltip title="Copiar ID de la empresa">
                  <IconButton
                    size="small"
                    aria-label="Copiar ID de la empresa"
                    onClick={() => {
                      void navigator.clipboard.writeText(companyId);
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Autocomplete
                disablePortal
                options={assignablePodcasts}
                loading={assignableLoading}
                getOptionLabel={(option: Podcast) => option.title}
                value={selectedPodcast}
                onChange={(_e, value) => setSelectedPodcast(value)}
                inputValue={podcastSearch}
                onInputChange={(_e, value) => setPodcastSearch(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar podcast" size="small" />
                )}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                onClick={() => addPodcastMutation.mutate()}
                disabled={addPodcastMutation.isPending || !selectedPodcast}
              >
                Añadir Podcast
              </Button>
            </Stack>
          </Box>

          <Stack spacing={1}>
            {companyPodcasts.map((p) => {
              const programs = (p.programs || []) as CompanyPodcastProgramRow[];
              return (
                <Accordion key={p._id} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: "100%", flexWrap: "wrap" }}
                    >
                      <Typography fontWeight="medium">{p.title}</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {p._id}
                        </Typography>
                        <Tooltip title="Copiar ID del podcast">
                          <IconButton
                            size="small"
                            aria-label="Copiar ID del podcast"
                            onClick={(e) => {
                              e.stopPropagation();
                              void navigator.clipboard.writeText(p._id);
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {p.category && (
                        <Chip
                          label={p.category}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="body2" color="text.secondary">
                            Redes:
                          </Typography>
                          {p.socialAccounts?.instagram && (
                            <Chip
                              icon={<InstagramIcon />}
                              label={p.socialAccounts.instagram}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                          {p.socialAccounts?.tiktok && (
                            <Chip
                              icon={<TikTokIcon />}
                              label={p.socialAccounts.tiktok}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                          {p.socialAccounts?.facebook && (
                            <Chip
                              icon={<FacebookIcon />}
                              label={p.socialAccounts.facebook}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleOpenSocialAccountsDialog(p)}
                            title="Editar handles en perfil (Instagram/TikTok/Facebook del podcast)"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removePodcastMutation.mutate(p._id)}
                          disabled={removePodcastMutation.isPending}
                          title="Quitar podcast de la empresa"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>

                      {companyId && (
                        <PodcastOAuthConnectionsAdmin
                          podcast={p}
                          companyId={companyId}
                        />
                      )}

                      {companyId && (
                        <Box sx={{ pb: 1 }}>
                          <PodcastLivestreamToggle
                            podcast={p}
                            companyId={companyId}
                            onError={setError}
                          />
                        </Box>
                      )}

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Programa</TableCell>
                            <TableCell align="center">Habilitado</TableCell>
                            <TableCell align="center">
                              Datos livestream
                            </TableCell>
                            <TableCell align="center">Demográficos</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {programs.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No hay programas para este podcast
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            programs.map((prog) => (
                              <TableRow key={prog._id}>
                                <TableCell>{prog.name}</TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={prog.enabled}
                                    onChange={(e) =>
                                      updateProgramSettingsMutation.mutate({
                                        programId: prog._id,
                                        enabled: e.target.checked,
                                      })
                                    }
                                    disabled={
                                      updateProgramSettingsMutation.isPending
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={prog.livestreamDataEnabled}
                                    onChange={(e) =>
                                      updateProgramSettingsMutation.mutate({
                                        programId: prog._id,
                                        livestreamDataEnabled: e.target.checked,
                                      })
                                    }
                                    disabled={
                                      updateProgramSettingsMutation.isPending
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={prog.demographicDataEnabled}
                                    onChange={(e) =>
                                      updateProgramSettingsMutation.mutate({
                                        programId: prog._id,
                                        demographicDataEnabled:
                                          e.target.checked,
                                      })
                                    }
                                    disabled={
                                      updateProgramSettingsMutation.isPending
                                    }
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>

                      {/* Live API Discovery Schedules */}
                      {(() => {
                        const DAY_NAMES = [
                          "Dom",
                          "Lun",
                          "Mar",
                          "Mié",
                          "Jue",
                          "Vie",
                          "Sáb",
                        ];
                        const ytId = (p as any).youtubeData?.channelId;
                        const channel = ytId
                          ? channelByYoutubeId.get(ytId)
                          : undefined;
                        if (!channel) return null;

                        const schedules = channel.liveSchedules ?? [];
                        const formData = scheduleFormData[channel._id] ?? {
                          days: [],
                          times: ["22:00"],
                        };
                        const isFormOpen = !!scheduleFormOpen[channel._id];
                        const isSaving = addScheduleMutation.isPending;
                        const slotCount =
                          formData.days.length * formData.times.length;

                        return (
                          <Box
                            sx={{
                              mt: 1,
                              pt: 2,
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <ScheduleIcon
                                  sx={{ fontSize: 16, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  API Discovery Schedules
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ fontFamily: "monospace" }}
                                >
                                  {channel.channelName}
                                </Typography>
                              </Box>
                              {!isFormOpen && (
                                <Tooltip title="Agregar horarios">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      openScheduleForm(channel._id)
                                    }
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>

                            {schedules.length === 0 && !isFormOpen && (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                Sin horarios configurados. El worker de API no
                                verificará este canal.
                              </Typography>
                            )}

                            {schedules.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  mb: 1,
                                }}
                              >
                                {schedules.map((slot) => (
                                  <Chip
                                    key={slot.programName}
                                    label={`${DAY_NAMES[slot.dayOfWeek]} ${slot.hour.toString().padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`}
                                    size="small"
                                    onDelete={() =>
                                      deleteScheduleMutation.mutate({
                                        channelId: channel._id,
                                        programName: slot.programName,
                                      })
                                    }
                                    disabled={deleteScheduleMutation.isPending}
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}

                            {isFormOpen && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 2,
                                  bgcolor: "background.default",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Stack spacing={2}>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ mb: 0.5 }}
                                    >
                                      Días
                                    </Typography>
                                    <ToggleButtonGroup
                                      value={formData.days}
                                      onChange={(_e, newDays) =>
                                        setScheduleFormData((prev) => ({
                                          ...prev,
                                          [channel._id]: {
                                            ...formData,
                                            days: newDays,
                                          },
                                        }))
                                      }
                                      size="small"
                                      sx={{ flexWrap: "wrap" }}
                                    >
                                      {DAY_NAMES.map((name, idx) => (
                                        <ToggleButton
                                          key={idx}
                                          value={idx}
                                          sx={{ px: 1.5, py: 0.5 }}
                                        >
                                          {name}
                                        </ToggleButton>
                                      ))}
                                    </ToggleButtonGroup>
                                  </Box>

                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ mb: 0.5 }}
                                    >
                                      Horarios (Hora de Chile – Santiago)
                                    </Typography>
                                    <Stack spacing={1}>
                                      {formData.times.map((t, idx) => (
                                        <Stack
                                          key={idx}
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                        >
                                          <TextField
                                            type="time"
                                            size="small"
                                            value={t}
                                            onChange={(e) => {
                                              const newTimes = [
                                                ...formData.times,
                                              ];
                                              newTimes[idx] = e.target.value;
                                              setScheduleFormData((prev) => ({
                                                ...prev,
                                                [channel._id]: {
                                                  ...formData,
                                                  times: newTimes,
                                                },
                                              }));
                                            }}
                                            inputProps={{ step: 300 }}
                                            sx={{ width: 130 }}
                                          />
                                          {formData.times.length > 1 && (
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                const newTimes =
                                                  formData.times.filter(
                                                    (_, i) => i !== idx,
                                                  );
                                                setScheduleFormData((prev) => ({
                                                  ...prev,
                                                  [channel._id]: {
                                                    ...formData,
                                                    times: newTimes,
                                                  },
                                                }));
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Stack>
                                      ))}
                                      <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() =>
                                          setScheduleFormData((prev) => ({
                                            ...prev,
                                            [channel._id]: {
                                              ...formData,
                                              times: [
                                                ...formData.times,
                                                "10:00",
                                              ],
                                            },
                                          }))
                                        }
                                        sx={{ alignSelf: "flex-start" }}
                                      >
                                        Agregar hora
                                      </Button>
                                    </Stack>
                                  </Box>

                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        closeScheduleForm(channel._id)
                                      }
                                    >
                                      Cancelar
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      disabled={
                                        isSaving ||
                                        formData.days.length === 0 ||
                                        formData.times.length === 0
                                      }
                                      onClick={() =>
                                        addScheduleMutation.mutate({
                                          channelId: channel._id,
                                          days: formData.days,
                                          times: formData.times,
                                        })
                                      }
                                    >
                                      {isSaving
                                        ? "Guardando..."
                                        : `Guardar (${slotCount} slot${slotCount !== 1 ? "s" : ""})`}
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Paper>

        {/* Social Accounts Dialog */}
        <Dialog
          open={socialAccountsDialog.open}
          onClose={handleCloseSocialAccountsDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Editar Redes Sociales
            {socialAccountsDialog.podcast && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {socialAccountsDialog.podcast.title}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Instagram"
                placeholder="usuario"
                value={socialAccountsDialog.instagram}
                onChange={(e) =>
                  setSocialAccountsDialog((prev) => ({
                    ...prev,
                    instagram: e.target.value,
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <InstagramIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                helperText="Ingresa solo el nombre de usuario (sin @)"
              />
              <TextField
                fullWidth
                label="TikTok"
                placeholder="usuario"
                value={socialAccountsDialog.tiktok}
                onChange={(e) =>
                  setSocialAccountsDialog((prev) => ({
                    ...prev,
                    tiktok: e.target.value,
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <TikTokIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                helperText="Ingresa solo el nombre de usuario (sin @)"
              />
              <TextField
                fullWidth
                label="Facebook"
                placeholder="pagename o URL"
                value={socialAccountsDialog.facebook}
                onChange={(e) =>
                  setSocialAccountsDialog((prev) => ({
                    ...prev,
                    facebook: e.target.value,
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <FacebookIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                helperText="Nombre de la Page (cnnchile) o URL completa"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSocialAccountsDialog}>Cancelar</Button>
            <Button
              onClick={handleSaveSocialAccounts}
              variant="contained"
              disabled={updateSocialAccountsMutation.isPending}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!generatedInviteUrl}
          onClose={() => {
            setGeneratedInviteUrl(null);
            setInviteUrlCopied(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {generatedUrlKind === "demo"
              ? "Enlace de acceso demo"
              : "Enlace de invitación"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {generatedUrlKind === "demo"
                ? "Abre el login con el email y la contraseña ya completados. Es reutilizable; puedes volver a copiarlo desde la tabla de usuarios. Crear la cuenta demo de nuevo con el mismo email invalida este enlace."
                : "Copia este enlace y compártelo. Solo se muestra una vez y es de un solo uso (caduca en 1 mes)."}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={generatedInviteUrl || ""}
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setGeneratedInviteUrl(null);
                setInviteUrlCopied(false);
              }}
            >
              Cerrar
            </Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                if (!generatedInviteUrl) return;
                try {
                  await navigator.clipboard.writeText(generatedInviteUrl);
                  setInviteUrlCopied(true);
                } catch {
                  setError("No se pudo copiar al portapapeles");
                }
              }}
            >
              {inviteUrlCopied ? "Copiado" : "Copiar enlace"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!pendingRevokeDemoInvite}
          onClose={() => setPendingRevokeDemoInvite(null)}
        >
          <DialogTitle>Eliminar invitación demo</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Eliminar la invitación de {pendingRevokeDemoInvite?.email}? El
              enlace enviado o copiado deja de funcionar. La cuenta se mantiene
              y puedes enviar una invitación nueva.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingRevokeDemoInvite(null)}>
              Cancelar
            </Button>
            <Button
              color="warning"
              variant="contained"
              disabled={revokeDemoInviteMutation.isPending}
              onClick={() =>
                pendingRevokeDemoInvite &&
                revokeDemoInviteMutation.mutate(pendingRevokeDemoInvite._id)
              }
            >
              {revokeDemoInviteMutation.isPending ? "Eliminando…" : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!pendingDeleteDemoUser}
          onClose={() => setPendingDeleteDemoUser(null)}
        >
          <DialogTitle>Eliminar cuenta demo</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Eliminar la cuenta demo {pendingDeleteDemoUser?.email}? Se borra
              el usuario y su enlace de acceso deja de funcionar.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingDeleteDemoUser(null)}>
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteDemoUserMutation.isPending}
              onClick={() =>
                pendingDeleteDemoUser &&
                deleteDemoUserMutation.mutate(pendingDeleteDemoUser._id)
              }
            >
              {deleteDemoUserMutation.isPending ? "Eliminando…" : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={localhostInviteSnackbarOpen}
          autoHideDuration={6000}
          onClose={(_e, reason) => {
            if (reason === "clickaway") return;
            setLocalhostInviteSnackbarOpen(false);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setLocalhostInviteSnackbarOpen(false)}
            severity="info"
            sx={{ width: "100%" }}
          >
            Las invitaciones por email no están disponibles en localhost. Usa un
            entorno desplegado para probar el envío.
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default CompanyConfig;
